// Keyboard play mapping. Melodic instruments use a scale-degree row; the drum
// kit uses the Z X C V B N pads. Live notes flow through ctx.play() (which both
// sounds the note and captures it while recording). docs/05 §5.4.

import type { InstrumentDef } from "../instruments/registry.ts";
import type { NoteEvent } from "../util/types.ts";

export interface PlayContext {
  getActiveDef(): InstrumentDef;
  play(ev: NoteEvent): void;
}

const MELODIC_KEYS: Record<string, number> = {
  KeyA: 0, KeyS: 1, KeyD: 2, KeyF: 3, KeyG: 4, KeyH: 5, KeyJ: 6, KeyK: 7, KeyL: 8,
};

const DRUM_KEYS: Record<string, string> = {
  KeyZ: "kick", KeyX: "snare", KeyC: "hat", KeyV: "tom", KeyB: "clap", KeyN: "crash",
};

const KEY_VELOCITY = 0.85;

export class KeyboardInput {
  constructor(private readonly ctx: PlayContext) {}

  attach(): void {
    document.addEventListener("keydown", this.onKeydown);
  }

  detach(): void {
    document.removeEventListener("keydown", this.onKeydown);
  }

  private readonly onKeydown = (e: KeyboardEvent): void => {
    if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
      return;
    }

    const def = this.ctx.getActiveDef();
    if (def.scaleLocked) {
      const degree = MELODIC_KEYS[e.code];
      if (degree === undefined) return;
      e.preventDefault();
      this.ctx.play({ degree, velocity: KEY_VELOCITY, time: 0 });
    } else {
      const voice = DRUM_KEYS[e.code];
      if (!voice) return;
      e.preventDefault();
      this.ctx.play({ note: voice, velocity: KEY_VELOCITY, time: 0 });
    }
  };
}
